import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMapMarkers, saveMapMarkers } from '../../api/wikiApi';
import styles from './WikiMapEditor.module.css';
import { MapPin, Trash2, Save, Plus, Eye, EyeOff, RotateCcw } from 'lucide-react';

const CATEGORY_ICONS = {
  bank:       { emoji: '🏦', label: 'Банк',                    color: '#ffd700' },
  shop:       { emoji: '🛒', label: 'Магазин',                  color: '#00d4ff' },
  blacksmith: { emoji: '⚒️',  label: 'Кузнец',                  color: '#ff8c00' },
  spawn:      { emoji: '🏠', label: 'Спавн',                    color: '#32cd32' },
  landmark:   { emoji: '🏛️',  label: 'Достопримечательность',   color: '#ff88cc' },
  quest:      { emoji: '❗',  label: 'Квест',                   color: '#ffff00' },
  other:      { emoji: '📍', label: 'Другое',                   color: '#ffffff' },
};

function genId() {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

const EMPTY_FORM = { title: '', description: '', category: 'other' };

export default function WikiMapEditor() {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [placingMode, setPlacingMode] = useState(false);
  const [pendingPos, setPendingPos] = useState(null); // { x%, y% }
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    getMapMarkers().then((data) => {
      setMarkers(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  }, []);

  const handleImgLoad = () => {
    if (imgRef.current) {
      setImgSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  };

  // ——— Zoom ———
  const handleWheel = useCallback((e) => {
    if (placingMode) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.min(4, Math.max(0.3, z + delta)));
  }, [placingMode]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ——— Pan (только когда не в режиме расстановки) ———
  const handleMouseDown = (e) => {
    if (placingMode || e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragStart) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  // ——— Клик по карте для расстановки метки ———
  const handleMapClick = (e) => {
    if (!placingMode) return;
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPos({ x: +x.toFixed(2), y: +y.toFixed(2) });
    setForm(EMPTY_FORM);
    setEditingId(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  // ——— Сохранить новую / обновить метку ———
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) return;

    if (editingId) {
      setMarkers((prev) =>
        prev.map((m) => m.id === editingId ? { ...m, ...form, title } : m)
      );
      setEditingId(null);
    } else if (pendingPos) {
      const newMarker = {
        id: genId(),
        x: pendingPos.x,
        y: pendingPos.y,
        title,
        description: form.description.trim(),
        category: form.category,
      };
      setMarkers((prev) => [...prev, newMarker]);
      setPendingPos(null);
      setPlacingMode(false);
    }
    setForm(EMPTY_FORM);
  };

  const handleCancelForm = () => {
    setPendingPos(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setPlacingMode(false);
  };

  const handleEditMarker = (marker) => {
    setEditingId(marker.id);
    setForm({ title: marker.title, description: marker.description || '', category: marker.category || 'other' });
    setPendingPos(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  const handleDeleteMarker = (id) => {
    setMarkers((prev) => prev.filter((m) => m.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setForm(EMPTY_FORM);
    }
  };

  // ——— Сохранить всё на сервер ———
  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await saveMapMarkers(markers);
      setSaveMsg('✅ Сохранено!');
    } catch (err) {
      setSaveMsg(`❌ Ошибка: ${err.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 3000);
    }
  };

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const showForm = pendingPos !== null || editingId !== null;

  return (
    <div className={styles.editor}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <MapPin size={20} className={styles.topBarIcon} />
          <h2 className={styles.topBarTitle}>Редактор карты</h2>
          <span className={styles.markerCount}>{markers.length} меток</span>
        </div>
        <div className={styles.topBarActions}>
          <button
            className={`${styles.actionBtn} ${placingMode ? styles.actionBtnActive : ''}`}
            onClick={() => { setPlacingMode((v) => !v); setPendingPos(null); setEditingId(null); setForm(EMPTY_FORM); }}
            title={placingMode ? 'Отменить расстановку' : 'Добавить метку (кликните на карту)'}
          >
            <Plus size={16} />
            {placingMode ? 'Отмена' : 'Добавить метку'}
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => setShowLabels((v) => !v)}
            title={showLabels ? 'Скрыть подписи' : 'Показать подписи'}
          >
            {showLabels ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            <Save size={16} />
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          {saveMsg && <span className={styles.saveMsg}>{saveMsg}</span>}
        </div>
      </div>

      {placingMode && (
        <div className={styles.placingBanner}>
          📍 Кликните на карту, чтобы поставить метку
        </div>
      )}

      <div className={styles.workspace}>
        {/* ——— Карта ——— */}
        <div className={styles.mapWrapper}>
          <div
            ref={containerRef}
            className={`${styles.mapContainer} ${placingMode ? styles.mapContainerPlacing : ''} ${isDragging ? styles.dragging : ''}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleMapClick}
          >
            <div
              className={styles.mapInner}
              style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
            >
              <img
                ref={imgRef}
                src="/map.png"
                alt="Карта"
                className={styles.mapImage}
                onLoad={handleImgLoad}
                draggable={false}
              />

              {/* Существующие метки */}
              {!loading && markers.map((marker) => {
                const cat = CATEGORY_ICONS[marker.category] || CATEGORY_ICONS.other;
                const isEditing = editingId === marker.id;
                return (
                  <div
                    key={marker.id}
                    className={`${styles.marker} ${isEditing ? styles.markerEditing : ''}`}
                    style={{ left: `${marker.x}%`, top: `${marker.y}%`, '--marker-color': cat.color }}
                    title={marker.title}
                  >
                    <div className={styles.markerPin}>
                      <span className={styles.markerEmoji}>{cat.emoji}</span>
                    </div>
                    {showLabels && (
                      <span className={styles.markerLabel}>{marker.title}</span>
                    )}
                    <div className={styles.markerActions}>
                      <button
                        className={styles.markerActionBtn}
                        onClick={(e) => { e.stopPropagation(); handleEditMarker(marker); }}
                        title="Редактировать"
                      >✏️</button>
                      <button
                        className={`${styles.markerActionBtn} ${styles.markerActionDelete}`}
                        onClick={(e) => { e.stopPropagation(); handleDeleteMarker(marker.id); }}
                        title="Удалить"
                      >🗑️</button>
                    </div>
                  </div>
                );
              })}

              {/* Предварительная метка */}
              {pendingPos && (
                <div
                  className={`${styles.marker} ${styles.markerPending}`}
                  style={{ left: `${pendingPos.x}%`, top: `${pendingPos.y}%`, '--marker-color': CATEGORY_ICONS[form.category]?.color || '#fff' }}
                >
                  <div className={styles.markerPin}>
                    <span className={styles.markerEmoji}>{CATEGORY_ICONS[form.category]?.emoji || '📍'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Контролы зума */}
          <div className={styles.zoomControls}>
            <button className={styles.zoomBtn} onClick={() => setZoom((z) => Math.min(4, z + 0.2))} title="Приблизить">+</button>
            <button className={styles.zoomBtn} onClick={resetView} title="Сбросить"><RotateCcw size={14} /></button>
            <button className={styles.zoomBtn} onClick={() => setZoom((z) => Math.max(0.3, z - 0.2))} title="Отдалить">−</button>
          </div>

          {loading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.spinner} />
              <span>Загрузка...</span>
            </div>
          )}
        </div>

        {/* ——— Боковая панель ——— */}
        <div className={styles.sidebar}>
          {/* Форма добавления / редактирования */}
          {showForm && (
            <div className={styles.formCard} ref={formRef}>
              <h3 className={styles.formTitle}>
                {editingId ? '✏️ Редактировать метку' : '📍 Новая метка'}
              </h3>
              {pendingPos && !editingId && (
                <p className={styles.formCoords}>
                  X: {pendingPos.x.toFixed(1)}% &nbsp; Y: {pendingPos.y.toFixed(1)}%
                </p>
              )}
              <form onSubmit={handleFormSubmit} className={styles.form}>
                <label className={styles.formLabel}>Название *</label>
                <input
                  className={styles.formInput}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Название метки"
                  required
                  autoFocus
                />

                <label className={styles.formLabel}>Описание</label>
                <textarea
                  className={styles.formTextarea}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Краткое описание (необязательно)"
                  rows={3}
                />

                <label className={styles.formLabel}>Категория</label>
                <div className={styles.categoryGrid}>
                  {Object.entries(CATEGORY_ICONS).map(([key, { emoji, label, color }]) => (
                    <button
                      key={key}
                      type="button"
                      className={`${styles.categoryBtn} ${form.category === key ? styles.categoryBtnActive : ''}`}
                      style={{ '--cat-color': color }}
                      onClick={() => setForm({ ...form, category: key })}
                      title={label}
                    >
                      <span>{emoji}</span>
                      <span className={styles.categoryBtnLabel}>{label}</span>
                    </button>
                  ))}
                </div>

                <div className={styles.formActions}>
                  <button type="button" className={styles.cancelBtn} onClick={handleCancelForm}>
                    Отмена
                  </button>
                  <button type="submit" className={styles.submitBtn}>
                    {editingId ? 'Обновить' : 'Добавить'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Список меток */}
          <div className={styles.markerList}>
            <h3 className={styles.listTitle}>
              <MapPin size={16} /> Все метки
            </h3>
            {markers.length === 0 ? (
              <p className={styles.listEmpty}>Меток пока нет. Нажмите «Добавить метку» и кликните на карту.</p>
            ) : (
              <ul className={styles.list}>
                {markers.map((marker) => {
                  const cat = CATEGORY_ICONS[marker.category] || CATEGORY_ICONS.other;
                  return (
                    <li
                      key={marker.id}
                      className={`${styles.listItem} ${editingId === marker.id ? styles.listItemActive : ''}`}
                    >
                      <span className={styles.listEmoji} style={{ color: cat.color }}>{cat.emoji}</span>
                      <div className={styles.listInfo}>
                        <span className={styles.listName}>{marker.title}</span>
                        <span className={styles.listCat}>{cat.label}</span>
                      </div>
                      <div className={styles.listActions}>
                        <button
                          className={styles.listBtn}
                          onClick={() => handleEditMarker(marker)}
                          title="Редактировать"
                        >✏️</button>
                        <button
                          className={`${styles.listBtn} ${styles.listBtnDelete}`}
                          onClick={() => handleDeleteMarker(marker.id)}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
